import { Box, Chip, IconButton, styled, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    EditButton,
    DateField,
    FunctionField,
    useRecordContext,
    useNotify,
    useDataProvider,
    Button,
    useRefresh,
    useListContext
} from "react-admin";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BlockIcon from '@mui/icons-material/Block'; // 禁用图标
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // 启用图标
import DeleteIcon from '@mui/icons-material/Delete'; // 删除图标
import PasswordVerifiers from "../deploy/passwordVerifiers";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UploadSystemImageButton = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法
    const [isUploading, setIsUploading] = useState(false); // 上传状态
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')); // 检测小屏幕

    // 处理文件上传
    const handleFileUpload = async (event: any) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        try {
            // 调用批量导入接口
            let { data } = await dataProvider.deployScript({
                file: file,
                id: record.id
            });
            refresh();  // 触发列表刷新
            notify(`部署流量脚本成功`, { type: 'success' });
        } catch (error: any) {
            notify(`部署流量脚本失败: ${error.message}`, { type: 'error' });
        } finally {
            setIsUploading(false);
            // 重置文件输入，允许重复上传同一文件
            event.target.value = null;
        }
    };

    return (
        <>
            <Button
                component="label"
                size="small"
                label="部署流量脚本"
                sx={{
                    fontSize: isSmallScreen ? 'revert' : 'revert', // 响应式字体大小
                    whiteSpace: 'nowrap', // 防止文字换行
                }}
            >
                <CloudUploadIcon fontSize="small" />
                <VisuallyHiddenInput
                    type="file"
                    accept="*/*"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                />
            </Button>
        </>
    );
};

export const StatusToggleButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    // 根据状态决定显示禁用还是启用按钮
    const isActive = record.status === 1;


    const handleStopTraffic = async () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const handleToggleStatus = () => {
        setLoading(true);
        const method = isActive ? 'disable' : 'enable';
        const successMessage = isActive ? '用户已禁用' : '用户已启用';

        dataProvider[method]('trafficAccount', { id: record.id })
            .then(() => {
                notify(successMessage, { type: 'success' });
                refresh();
                handleClosePasswordDialog()
            })
            .catch(error => {
                notify(`${isActive ? '禁用' : '启用'}失败: ${error.message}`, { type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    // 小屏幕使用图标按钮
    if (isSmallScreen) {
        return (
            <>
                <Tooltip title={isActive ? "禁用" : "启用"} arrow>
                    <IconButton
                        onClick={handleStopTraffic}
                        disabled={loading}
                        color={isActive ? "warning" : "success"}
                        size="small"
                    >
                        {isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                </Tooltip>
                <PasswordVerifiers
                    open={passwordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handleToggleStatus}
                    confirmLabel={isActive ? "禁用" : "启用"}
                />
            </>
        );
    }

    // 大屏幕使用带图标的文字按钮
    return (
        <>
            <Button
                label={isActive ? "禁用" : "启用"}
                onClick={handleStopTraffic}
                disabled={loading}
                color={isActive ? "warning" : "success"}
                startIcon={isActive ? <BlockIcon /> : <CheckCircleIcon />}
                sx={{
                    fontSize: '14px',
                    padding: '6px 12px',
                    minWidth: 'auto',
                }}
            />
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleToggleStatus}
                confirmLabel={isActive ? "禁用" : "启用"}
            />
        </>
    );
};

export const DeleteWithConfirmButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const handleDeleteClick = () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const handleDelete = () => {
        setLoading(true);
        dataProvider.delete('trafficAccount', { id: record.id })
            .then(() => {
                notify(`账号 ${record.name} 已删除`, { type: 'success' });
                refresh();
            })
            .catch(error => {
                notify(`删除失败: ${error.message}`, { type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    // 小屏幕使用图标按钮
    if (isSmallScreen) {
        return (
            <>
                <Tooltip title="删除" arrow>
                    <IconButton
                        onClick={handleDeleteClick}
                        disabled={loading}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
                <PasswordVerifiers
                    open={passwordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handleDelete}
                />
            </>
        );
    }

    // 大屏幕使用带图标的文字按钮
    return (
        <>
            <Button
                label="删除"
                onClick={handleDeleteClick}
                disabled={loading}
                color="error"
                startIcon={<DeleteIcon />}
                sx={{
                    fontSize: '14px',
                    padding: '6px 12px',
                    minWidth: 'auto',
                }}
            />
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleDelete}
            />
        </>
    );
};

const ListActions = () => {
    const { setFilters, filterValues } = useListContext();  // 获取列表上下文
    const initialFilters = {};  // 初始过滤器值
    const role = localStorage.getItem("permissions");
    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法

    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const exportDetails = () => {
        if (window.confirm(`确认导出流量销售账号列表？`)) {
            const formData = {
                ...filterValues,
            };
            dataProvider.traffiCustomersExport(formData)
                .then(() => {
                    notify('导出流量销售账号列表成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '导出流量销售账号列表失败', { type: 'error' });
                })
        }
    }

    return (
        <TopToolbar>
            <Button
                sx={{ fontSize: 'revert' }}
                onClick={handleResetFilters}
                disabled={Object.keys(filterValues).length === 0}  // 当没有过滤条件时禁用
            >
                重置搜索
            </Button>
            {role != 'ops' ? <>
                <CreateButton />
                <Button
                    label="导出"
                    onClick={exportDetails}
                /></> : null}
        </TopToolbar>
    );
};



export const TrafficAccountList = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [customerTypeChoices, setCustomerTypeChoices] = useState([]);
    const role = localStorage.getItem("permissions");

    useEffect(() => {
        // 异步获取客户类型数据
        const fetchCustomerTypes = async () => {
            try {
                const types = await dataProvider.getCustomerTypes();
                setCustomerTypeChoices(types);
            } catch (error) {
                console.error("获取客户类型失败:", error);
            }
        };

        fetchCustomerTypes();
    }, []);  // 空依赖数组确保只调用一次

    let postFilters = [
        <SelectInput
            source="customerTypeId"
            alwaysOn
            label="客户类型"
            choices={customerTypeChoices}
        />,
        <TextInput label="客户名称" alwaysOn source="name" />,
        <TextInput label="联系电话" alwaysOn source="contactPhone" />
    ];

    return (
        <List filters={postFilters} actions={<ListActions />} empty={false}>
            <Datagrid bulkActionButtons={false} rowClick={false} rowStyle={(record) => ({
                backgroundColor: record.status === 0 ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                opacity: record.status === 0 ? 0.7 : 1
            })}>
                <TextField source="id" label="客户ID" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="customerTypeName" label="客户类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="name" label="客户名称" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                {/* <ImageField source="logo" label="LOGO" sx={{ display: 'flex', '& img': { maxWidth: 50, maxHeight: 50, objectFit: 'contain' } }} /> */}
                <TextField source="manager" label="负责人" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="contactPhone" label="联系电话" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    sortable={false}
                    label="合同状态"
                    render={(record) => {
                        if (!record.contractEndDate) {
                            return null;
                        }

                        const endTime = new Date(record.contractEndDate);
                        const now = new Date();
                        // 比较日期（忽略时间部分）
                        const endDate = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate());
                        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const isActive = endDate >= currentDate;
                        return (
                            <Chip
                                label={isActive ? "正常" : "失效"}
                                color={isActive ? "success" : 'error'}
                                size="small"
                            />
                        );
                    }}
                />
                <DateField source="createTime" label="创建日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="snRule" label="sn规则" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    sortable={false}
                    label="文件名称"
                    render={(record) => {
                        if (!record.scriptUrl) {
                            return null;
                        }
                        // 从 URL 中提取版本号
                        const url = record.scriptUrl;
                        const fileName = url.split('/').pop(); // 获取文件名部分
                        // if (!fileName) return null;

                        // // 使用正则表达式匹配版本号格式 x.x.x
                        // const versionMatch = fileName.match(/(\d+\.\d+\.\d+)/);
                        // let version = versionMatch ? versionMatch[1] : '未知版本';

                        return fileName || '';
                    }}
                />
                {role != 'ops' ? <FunctionField
                    sortable={false}
                    label="操作"
                    render={(record) => (
                        <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                            <UploadSystemImageButton />

                            <EditButton />
                            <StatusToggleButton />
                            <DeleteWithConfirmButton />
                        </Box>
                    )}
                /> : null}

            </Datagrid>
        </List>
    )
}